import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Image, Tooltip } from "zhihao-ui-react";

export interface TableImageCellProps {
    /** 图片的无障碍名称。 */
    alt: string;
    /** 图片加载失败或缺失时展示的占位文案。 */
    emptyText?: string;
    /** 表格图片边长，默认沿用旧 Vue 商品表格的 65px。 */
    size?: number;
    /** 图片地址。 */
    src?: string;
    /** 图片右侧的标题或补充内容。 */
    title?: ReactNode;
    /** 溢出悬浮提示内容；未传入时沿用标题节点。 */
    tooltipTitle?: ReactNode;
}

/**
 * 表格图片单元格的公共渲染契约：统一尺寸、预览、标题截断和加载失败占位。
 */
export function TableImageCell({
    alt,
    emptyText = "暂无图片",
    size = 65,
    src,
    title,
    tooltipTitle
}: TableImageCellProps) {
    const [loadFailed, setLoadFailed] = useState(false);

    // 行复用或图片地址更新后允许新地址重新加载，避免保留上一张图片的失败状态。
    useEffect(() => {
        setLoadFailed(false);
    }, [src]);

    return (
        <div className="admin-table-image-cell">
            {src && !loadFailed ? (
                <Image
                    alt={alt}
                    className="admin-table-image-cell__image"
                    fit="contain"
                    height={size}
                    previewSrcList={[src]}
                    previewTeleported
                    src={src}
                    width={size}
                    onError={() => setLoadFailed(true)}
                />
            ) : (
                <span className="admin-table-image-cell__placeholder" style={{ width: size, height: size }}>
                    {emptyText}
                </span>
            )}
            {title !== undefined && title !== null ? (
                // 旧 Vue 表格会为主图/标题单元格注入溢出提示；自定义渲染需显式保留该交互。
                <Tooltip title={tooltipTitle ?? title} placement="top">
                    <span className="admin-table-image-cell__title">{title}</span>
                </Tooltip>
            ) : null}
        </div>
    );
}

export default TableImageCell;
